import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  useLoginUser,
  useRegisterUser,
  useSignUpWhatsApp,
  useVerifyPhoneNewCode,
  useVerifyUser,
} from '~api/auth/authMutations';
import { UserRegisterInput } from '~api/auth/types';
import { DIALOGS } from '~components/atoms/AbsoluteDialogs';
import { Box } from '~components/atoms/Box';
import { Loader } from '~components/atoms/Loader';
import { Text } from '~components/atoms/Typography';
import { SIGNUP_FORM_DEFAULT_VALUES } from '~constants/auth';
import {
  HTTP_STATUS_CODES,
  INVITE_CODE_KEY,
  REF_CODE_KEY,
} from '~constants/common';
import { usePhoneNumber } from '~hooks/usePhoneNumber';
import { useTranslation } from '~hooks/useTranslation';
import { useAppDispatch } from '~store';
import { closeDialog, openDialog } from '~store/slices/globalDialogSlice';
import { setSessionId } from '~store/slices/signalRSocketsSlice';
import { setIsUserBlocked } from '~store/slices/userSlice';
import { BACKEND_ERROR_CODES } from '~utils/backendErrors';
import { COOKIES_NAMES, setCookie } from '~utils/cookies';

import {
  SIGNIN_FORM_FIELDS,
  SIGNUP_FORM_FIELDS,
  SignupFormInputs,
} from '../../types';
import { AuthTabs, StyledAuthTabsContent } from '../AuthTabs';
import { AuthTemplate } from '../AuthTemplate';

import { ProvideDetailsTab } from './ProvideDetailsTab';
import { SIGN_UP_TABS } from './SignUpTabList';
import { VerifyNumberTab } from './VerifyNumberTab';

export const SignUpContent = () => {
  const [activeTab, setActiveTab] = useState(SIGN_UP_TABS.PROVIDE_DETAILS);
  const [isRegisteringWhatsApp, setIsRegisteringWhatsApp] = useState(false);
  const dispatch = useAppDispatch();
  const formMethods = useForm<SignupFormInputs>({
    defaultValues: SIGNUP_FORM_DEFAULT_VALUES,
  });
  const { localizedError, localized } = useTranslation();
  const { getValues, setError, clearErrors } = formMethods;

  const {
    verifyUserMutation,
    verifyUserIsLoading,
    verifyUserIsSuccess,
    verifyUserIsError,
    verifyUserErrorMessage,
  } = useVerifyUser();
  const {
    signUpWhatsAppMutation,
    signUpWhatsAppIsLoading,
    signUpWhatsAppIsError,
    signUpWhatsAppErrorMessage,
  } = useSignUpWhatsApp();

  const {
    registerUserMutation,
    registerUserIsLoading,
    registerUserIsError,
    registerUserErrorMessage,
    registerUserData,
    registerUserErrorCode,
  } = useRegisterUser();
  const { loginUserMutation, loginUserData, loginUserIsSuccess } =
    useLoginUser();
  const {
    verifyPhoneNewCodeMutation,
    verifyPhoneNewCodeData,
    verifyPhoneNewCodeIsSuccess,
    verifyPhoneNewCodeIsLoading,
  } = useVerifyPhoneNewCode();
  const { getPhoneNumberWithoutCode, getPhoneNumberWithCode } =
    usePhoneNumber();

  const goToTab = (tabName: SIGN_UP_TABS) => {
    setActiveTab(tabName);
  };

  const handleNextTab = async () => {
    try {
      const refCode = localStorage.getItem(REF_CODE_KEY);
      const inviteCode = localStorage.getItem(INVITE_CODE_KEY);
      const phoneNumberVal = getValues(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD);

      const phoneWithoutCode = phoneNumberVal.startsWith('+')
        ? getPhoneNumberWithoutCode(phoneNumberVal)
        : phoneNumberVal;
      const startsWithZero = phoneWithoutCode.startsWith('0');
      const phoneNumber = startsWithZero
        ? phoneWithoutCode.slice(1)
        : phoneWithoutCode;

      const payload: Record<string, string> = {
        userName: getValues(SIGNUP_FORM_FIELDS.USERNAME_FIELD),
        password: getValues(SIGNUP_FORM_FIELDS.PASSWORD_FIELD),
        phoneNumber: getPhoneNumberWithCode(phoneNumber),
        promoCode: getValues(SIGNUP_FORM_FIELDS.PROMOTION_CODE_FIELD),
      };

      if (refCode && refCode.length) {
        payload.referenceCode = refCode;
      }

      if (inviteCode && inviteCode.length) {
        payload.referralUserId = inviteCode;
      }

      const resultPayload: Record<string, string> = {};

      const parsedPayloadKeys = Object.keys(payload).filter(
        (key) => (payload[key] || '').length,
      );

      parsedPayloadKeys.forEach((key) => {
        resultPayload[key] = payload[key] || '';
      });

      const result = await registerUserMutation(
        resultPayload as unknown as UserRegisterInput,
      ).unwrap();
      const { verificationCodeExpiration } = result;

      if (refCode && refCode.length) {
        localStorage.removeItem(REF_CODE_KEY);
      }

      if (inviteCode && inviteCode.length) {
        localStorage.removeItem(INVITE_CODE_KEY);
      }

      setCookie(
        COOKIES_NAMES.VERIFICATION_CODE_EXPIRATION,
        verificationCodeExpiration,
        {
          expires: new Date(verificationCodeExpiration),
        },
      );
      goToTab(SIGN_UP_TABS.VERIFY_NUMBER);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (['userDeactivated', 'userBlocked'].includes(err.data.message[0])) {
        dispatch(openDialog(DIALOGS.DEACTIVATED_ACCOUNT));
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dispatch(setIsUserBlocked(err.data.message[0] === 'userBlocked'));
      }

      console.error('Failed to register user: ', err);
    }
  };

  const handleVerifyPhoneNewCode = async () => {
    const phoneNumberVal = getValues(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD);

    const phoneWithoutCode = phoneNumberVal.startsWith('+')
      ? getPhoneNumberWithoutCode(phoneNumberVal)
      : phoneNumberVal;
    const startsWithZero = phoneWithoutCode.startsWith('0');
    const phoneNumber = startsWithZero
      ? phoneWithoutCode.slice(1)
      : phoneWithoutCode;

    try {
      await verifyPhoneNewCodeMutation({
        phoneNumber: getPhoneNumberWithCode(phoneNumber),
      }).unwrap();
    } catch (err) {
      console.error('Failed to verify phone with new code: ', err);
    }
  };

  const handleSignIn = () => {
    dispatch(openDialog(DIALOGS.SIGN_IN));
  };

  const handleRegisterUser = async () => {
    clearErrors();
    const isAgreed = getValues(SIGNUP_FORM_FIELDS.AGREE_TO_TERMS_FIELD);

    if (!isAgreed) {
      setError(SIGNUP_FORM_FIELDS.AGREE_TO_TERMS_FIELD, {
        type: 'required',
        message: localizedError('agreeToTerms.mustBeTrue'),
      });

      return;
    }

    const phoneNumberVal = getValues(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD);
    const phoneWithoutCode = phoneNumberVal.startsWith('+')
      ? getPhoneNumberWithoutCode(phoneNumberVal)
      : phoneNumberVal;
    const startsWithZero = phoneWithoutCode.startsWith('0');
    const phoneNumber = startsWithZero
      ? phoneWithoutCode.slice(1)
      : phoneWithoutCode;

    try {
      await verifyUserMutation({
        phoneNumber: getPhoneNumberWithCode(phoneNumber),
        verificationCode: getValues(SIGNUP_FORM_FIELDS.NUMBER_CODE_FIELD),
      }).unwrap();
    } catch (err) {
      console.error('Failed to verify user: ', err);
    }
  };

  const handleSignInWithWhatsApp = async () => {
    clearErrors();
    const isAgreed = getValues(SIGNUP_FORM_FIELDS.AGREE_TO_TERMS_FIELD);

    if (!isAgreed) {
      setError(SIGNUP_FORM_FIELDS.AGREE_TO_TERMS_FIELD, {
        type: 'required',
        message: localizedError('agreeToTerms.mustBeTrue'),
      });
    }

    const name = getValues(SIGNIN_FORM_FIELDS.USERNAME_FIELD);

    if (!name.length) {
      setError(SIGNIN_FORM_FIELDS.USERNAME_FIELD, {
        type: 'required',
        message: localizedError('username.required'),
      });
    }

    const phoneNumberVal = getValues(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD);

    const phoneWithoutCode = phoneNumberVal.startsWith('+')
      ? getPhoneNumberWithoutCode(phoneNumberVal)
      : phoneNumberVal;

    if (!phoneWithoutCode.length) {
      setError(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD, {
        type: 'required',
        message: localizedError('phoneNumber.required'),
      });

      return;
    }

    const startsWithZero = phoneWithoutCode.startsWith('0');
    const phoneNumber = startsWithZero
      ? phoneWithoutCode.slice(1)
      : phoneWithoutCode;

    try {
      const response = await signUpWhatsAppMutation({
        phoneNumber: getPhoneNumberWithCode(phoneNumber),
        userName: getValues(SIGNIN_FORM_FIELDS.USERNAME_FIELD),
        promoCode: getValues(SIGNUP_FORM_FIELDS.PROMOTION_CODE_FIELD),
      });

      if ('error' in response) {
        setError('root.serverError', {
          type: HTTP_STATUS_CODES.BAD_REQUEST,
          message: localizedError('root.serverError'),
        });

        return;
      }

      const { sessionId } = response.data;

      dispatch(setSessionId(sessionId));

      setIsRegisteringWhatsApp(true);
    } catch (e) {
      setIsRegisteringWhatsApp(false);
    }
  };

  useEffect(() => {
    if (registerUserIsError) {
      if (
        registerUserErrorMessage === BACKEND_ERROR_CODES.DUPLICATE_USER_NAME ||
        registerUserErrorMessage === BACKEND_ERROR_CODES.USERNAME_ALREADY_EXISTS
      ) {
        setError(SIGNUP_FORM_FIELDS.USERNAME_FIELD, {
          type: BACKEND_ERROR_CODES.DUPLICATE_USER_NAME,
          message: localizedError('username.alreadyExists'),
        });
      }

      if (
        registerUserErrorMessage ===
        BACKEND_ERROR_CODES.PHONE_NUMBER_ALREADY_EXISTS
      ) {
        setError(SIGNUP_FORM_FIELDS.PHONE_NUMBER_FIELD, {
          type: BACKEND_ERROR_CODES.PHONE_NUMBER_ALREADY_EXISTS,
          message: localizedError('phoneNumber.alreadyExists'),
        });
      }

      if (registerUserErrorCode === HTTP_STATUS_CODES.BAD_REQUEST) {
        setError('root.incompleteOrIncorrectInfo', {
          type: HTTP_STATUS_CODES.BAD_REQUEST,
          message: registerUserErrorMessage,
        });
      }

      if (registerUserErrorCode === HTTP_STATUS_CODES.NOT_AVAILABLE) {
        setError('root.serverError', {
          type: HTTP_STATUS_CODES.BAD_REQUEST,
          message: localizedError('root.serverError'),
        });
      }
    }
  }, [registerUserIsError, registerUserErrorMessage]);

  useEffect(() => {
    if (verifyUserIsError) {
      if (
        verifyUserErrorMessage === BACKEND_ERROR_CODES.WRONG_VERIFICATION_CODE
      ) {
        setError(SIGNUP_FORM_FIELDS.NUMBER_CODE_FIELD, {
          type: BACKEND_ERROR_CODES.WRONG_VERIFICATION_CODE,
          message: localizedError('numberCode.incorrectCode'),
        });
      }

      if (verifyUserErrorMessage === BACKEND_ERROR_CODES.INVALID_PHONE_NUMBER) {
        setError(SIGNUP_FORM_FIELDS.PHONE_NUMBER_FIELD, {
          type: BACKEND_ERROR_CODES.INVALID_PHONE_NUMBER,
          message: localizedError('phoneNumber.invalid'),
        });
      }

      if (verifyUserErrorMessage === HTTP_STATUS_CODES.NOT_AVAILABLE) {
        setError('root.serverError', {
          type: HTTP_STATUS_CODES.BAD_REQUEST,
          message: localizedError('root.serverError'),
        });
      }
    }
  }, [verifyUserIsError, verifyUserErrorMessage]);

  useEffect(() => {
    if (signUpWhatsAppIsError) {
      if (
        signUpWhatsAppErrorMessage === BACKEND_ERROR_CODES.WHATSAPP_NOT_FOUND
      ) {
        setError(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD, {
          type: BACKEND_ERROR_CODES.WHATSAPP_NOT_FOUND,
          message: localizedError('phoneNumber.numberDoesntExistInWhatsapp'),
        });
      }

      if (
        signUpWhatsAppErrorMessage === BACKEND_ERROR_CODES.FAILEDS_SEND_MESSAGE
      ) {
        setError(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD, {
          type: BACKEND_ERROR_CODES.FAILEDS_SEND_MESSAGE,
          message: localizedError('phoneNumber.failedToSendWhatsAppMessage'),
        });
      }
    }
  }, [signUpWhatsAppIsError, signUpWhatsAppErrorMessage]);

  useEffect(() => {
    if (!verifyUserIsSuccess) {
      return;
    }

    const loginUser = async () => {
      try {
        const phoneNumberVal = getValues(SIGNIN_FORM_FIELDS.PHONE_NUMBER_FIELD);
        const phoneWithoutCode = phoneNumberVal.startsWith('+')
          ? getPhoneNumberWithoutCode(phoneNumberVal)
          : phoneNumberVal;
        const startsWithZero = phoneWithoutCode.startsWith('0');
        const phoneNumber = startsWithZero
          ? phoneWithoutCode.slice(1)
          : phoneWithoutCode;

        await loginUserMutation({
          phoneNumber: getPhoneNumberWithCode(phoneNumber),
          userName: getValues(SIGNIN_FORM_FIELDS.USERNAME_FIELD),
          password: getValues(SIGNIN_FORM_FIELDS.PASSWORD_FIELD),
        });
      } catch (err) {
        console.error('Failed to login user: ', err);
      }
    };

    loginUser();
  }, [verifyUserIsSuccess]);

  useEffect(() => {
    if (loginUserIsSuccess && loginUserData) {
      dispatch(closeDialog());
    }
  }, [loginUserIsSuccess, loginUserData]);

  if (isRegisteringWhatsApp) {
    return (
      <AuthTemplate>
        <Box flexCol gap={3} alignCenter justifyCenter fullWidth>
          <Loader
            css={{
              color: 'white',
              '& > svg': {
                width: '28px',
                height: '28px',
              },
            }}
          />
          <Text
            level="14-24"
            textTransform="uppercase"
            textAlign="center"
            css={{
              maxWidth: '419px',
            }}
          >
            {localized('signUp.followLink1')}{' '}
            <Text
              as="span"
              level="14-24"
              textTransform="uppercase"
              color="success"
            >
              {localized('signUp.whatsApp')}
            </Text>{' '}
            {localized('signUp.followLink2')}
          </Text>
        </Box>
      </AuthTemplate>
    );
  }

  return (
    <AuthTemplate>
      <AuthTabs activeTab={activeTab}>
        <FormProvider {...formMethods}>
          <form>
            <StyledAuthTabsContent value={SIGN_UP_TABS.PROVIDE_DETAILS}>
              <ProvideDetailsTab
                onNextTab={handleNextTab}
                onSignIn={handleSignIn}
                isRegisterUserLoading={registerUserIsLoading}
                isRegisterWhatsAppLoading={signUpWhatsAppIsLoading}
                handleSignInWithWhatsApp={handleSignInWithWhatsApp}
              />
            </StyledAuthTabsContent>
            <StyledAuthTabsContent value={SIGN_UP_TABS.VERIFY_NUMBER}>
              <VerifyNumberTab
                onSignIn={handleSignIn}
                onRegisterUser={handleRegisterUser}
                verifyUserIsLoading={verifyUserIsLoading}
                onVerifyPhoneNewCode={handleVerifyPhoneNewCode}
                registerUserData={registerUserData}
                verifyPhoneNewCodeData={verifyPhoneNewCodeData}
                verifyPhoneNewCodeIsSuccess={verifyPhoneNewCodeIsSuccess}
                verifyPhoneNewCodeIsLoading={verifyPhoneNewCodeIsLoading}
              />
            </StyledAuthTabsContent>
          </form>
        </FormProvider>
      </AuthTabs>
    </AuthTemplate>
  );
};
